import { z } from 'zod';
import { apiFetch } from './api';
import type { BulkSelection } from './email-accounts';

/**
 * Reports — warmup analytics for the dashboard.
 *
 * Backend source: `apps/api/src/modules/reports/reports.controller.ts`.
 * Both endpoints require `JwtAuthGuard + WorkspaceRoleGuard(VIEWER+) +
 * the (retired) analytics30d flag`. The API server caps `days` to 30 internally,
 * but we narrow the client surface to the documented period set so a malformed
 * value can't reach the wire.
 */
// DISABLED with the /dashboard route (its only user).
// export const REPORT_PERIOD_DAYS = [7, 14, 30] as const;
// export type ReportPeriodDays = (typeof REPORT_PERIOD_DAYS)[number];

/**
 * Day-count options for the `/email-accounts` listing page's "Export report"
 * dialog. Same values as `REPORT_PERIOD_DAYS` today, but a deliberately
 * SEPARATE constant — matches the backend's own `WARMUP_EXPORT_ALLOWED_DAYS`
 * (`apps/api/src/modules/reports/reports.constants.ts`), which is kept apart
 * from the dashboard's period toggle for the same reason: coupling an
 * unrelated dashboard chart's option set to this export's request contract
 * would mean a future edit to one silently changes the other, including
 * offering a day count the backend's `@IsIn` validator would 400 on.
 */
export const WARMUP_EXPORT_ALLOWED_DAYS = [7, 14, 30, 60, 90] as const;
export type WarmupExportDays = (typeof WARMUP_EXPORT_ALLOWED_DAYS)[number];

/**
 * Longest span (inclusive) the "Custom range" option accepts. Same value as
 * `MAX_ESP_WINDOW_DAYS` above (both ultimately bounded by `warmupEmails`'s
 * 90-day ClickHouse TTL) but deliberately a SEPARATE constant, not a shared
 * reference — matches the backend's own `WARMUP_EXPORT_MAX_CUSTOM_RANGE_DAYS`
 * (`apps/api/src/modules/reports/reports.constants.ts`), which is kept apart
 * from `MAX_ESP_ANALYTICS_WINDOW_DAYS` for the same reason: same coincidental
 * value today, different feature, independent validator.
 */
export const WARMUP_EXPORT_MAX_CUSTOM_RANGE_DAYS = 90;

// DISABLED with the /dashboard route (its only caller) — see routes/dashboard.tsx.
// export const dailyBreakdownItemSchema = z.object({
//   date: z.string(), // YYYY-MM-DD
//   sent: z.number(),
//   received: z.number(),
//   bounced: z.number(),
//   spamDetected: z.number(),
// });
//
// export const dashboardOverviewSchema = z.object({
//   totalAccounts: z.number(),
//   activeAccounts: z.number(),
//   totalSent: z.number(),
//   totalReceived: z.number(),
//   totalBounced: z.number(),
//   totalSpamDetected: z.number(),
//   averageDeliverabilityRate: z.number(), // 0..100
//   dailyBreakdown: z.array(dailyBreakdownItemSchema),
// });
//
// export type DailyBreakdownItem = z.infer<typeof dailyBreakdownItemSchema>;
// export type DashboardOverview = z.infer<typeof dashboardOverviewSchema>;
//
// export const leaderboardEntrySchema = z.object({
//   accountId: z.number(),
//   emailAddress: z.string(),
//   poolTier: z.string(),
//   warmupStatus: z.string(),
//   sent: z.number(),
//   received: z.number(),
//   bounced: z.number(),
//   spamDetected: z.number(),
//   deliverabilityRate: z.number(), // 0..100
// });
//
// export const leaderboardSchema = z.object({
//   entries: z.array(leaderboardEntrySchema),
// });
//
// export type LeaderboardEntry = z.infer<typeof leaderboardEntrySchema>;
// export type Leaderboard = z.infer<typeof leaderboardSchema>;
//
// export async function getWarmupOverview(
//   days: ReportPeriodDays,
// ): Promise<DashboardOverview> {
//   const raw = await apiFetch<unknown>(`/reports/warmup/overview?days=${days}`);
//   return dashboardOverviewSchema.parse(raw);
// }
//
// export async function getWarmupLeaderboard(
//   days: ReportPeriodDays,
//   limit: number,
// ): Promise<Leaderboard> {
//   const raw = await apiFetch<unknown>(
//     `/reports/warmup/leaderboard?days=${days}&limit=${limit}`,
//   );
//   return leaderboardSchema.parse(raw);
// }

// ── Warmup ESP analytics ─────────────────────────────────────────────────────
//
// Four `:accountId`-scoped panels behind `/reports/warmup/esp/*`, all gated by
// `FeatureGuard('analyticsFull')` (NOT the `analytics30d` the dashboard reads
// above). They are four routes rather than one payload precisely so each panel
// loads — and fails — independently; keep them as four query hooks.
//
// Backend source: `apps/api/src/modules/reports/reports.controller.ts` +
// `dto/esp-analytics.dto.ts`. Product context: spec
// `docs/specs/2026-08-19-warmup-esp-analytics.md`.

/**
 * ESP buckets exactly as they appear on the wire — the API's own display
 * vocabulary (`esp-analytics.constants.ts`'s `ESP_API_VALUE`), not the internal
 * ClickHouse one (`google`/`microsoft`).
 *
 * Three buckets, matching legacy one-for-one. A recipient whose ESP cannot be
 * resolved is written `other` server-side, so there is no fourth bucket to
 * render and the per-ESP rows always reconcile with the totals.
 */
export const ESP_VALUES = ['gmail', 'outlook', 'other'] as const;
export type EspValue = (typeof ESP_VALUES)[number];

/** Bucket widths the analytics time series accepts. */
export const ESP_GRANULARITIES = ['daily', 'weekly', 'monthly'] as const;
export type EspGranularity = (typeof ESP_GRANULARITIES)[number];

export const DEFAULT_ESP_GRANULARITY: EspGranularity = 'daily';

/**
 * Longest window these panels serve, as an INCLUSIVE span in days
 * (`from`..`to`, both ends counted).
 *
 * The server mirrors `MAX_ESP_ANALYTICS_WINDOW_DAYS = 90` in
 * `apps/api/src/modules/reports/esp-analytics.constants.ts` and throws
 * `070603` above it. `warmupEmails` carries a 90-day TTL that deletes
 * asynchronously on merge, so the oldest edge of a full-width window is
 * partially aged — do not promise "exactly 90 days" in UI copy.
 */
export const MAX_ESP_WINDOW_DAYS = 90;

const espValueSchema = z.enum(ESP_VALUES);

export const espWarmupStatsSchema = z.object({
  /**
   * The plan doesn't cover this panel (a 200, not a 403). Counts below stay real;
   * the panel blurs them on hover.
   */
  locked: z.boolean(),
  sentCount: z.number().nullable(),
  /**
   * Reply EVENTS, not "sends that got a reply" — two replies to one send count
   * as 2. Label it "Replies" wherever it renders.
   */
  replyCount: z.number().nullable(),
  inboxCount: z.number().nullable(),
  otherCount: z.number().nullable(),
  spamInboxCount: z.number().nullable(),
  undeliveredCount: z.number().nullable(),
  /**
   * Mail this account RECEIVED. The legacy misspelling is deliberate and is the
   * actual wire key — keep it here and rename only at the render boundary.
   *
   * Zeroes when the account's own ESP is not in the selected `esps`, by design
   * (a per-account constant gate reproduced from legacy). The other fields still
   * populate, so a lone 0 here is not a failure — see `EspStatsPanel`'s footnote.
   */
  recievedCount: z.number().nullable(),
  /** Already a percentage (0–100), computed server-side. Never recompute it. */
  deliverabilityRate: z.number().nullable(),
});

export const espDeliverabilityScoreSchema = z.object({
  /** See `espWarmupStatsSchema.locked`. Keyed on this panel's OWN capability
   * (`warmup-report/deliverability-score`), which an org can hold separately
   * from `warmup-report/esp-analytics`. */
  locked: z.boolean(),
  /** When `locked`: one row per requested ESP with every metric `null`, so the
   * table lists the ESPs and renders an em-dash per cell. Totals stay real. */
  perEsp: z.array(
    z.object({
      esp: espValueSchema,
      sentCount: z.number().nullable(),
      inboxCount: z.number().nullable(),
      spamCount: z.number().nullable(),
      deliverabilityRate: z.number().nullable(),
    }),
  ),
  totalSentCount: z.number().nullable(),
  totalInboxCount: z.number().nullable(),
  totalSpamCount: z.number().nullable(),
  deliverabilityRate: z.number().nullable(),
});

export const espBreakdownSchema = z.object({
  /** See `espWarmupStatsSchema.locked`. */
  locked: z.boolean(),
  /** When `locked`: one null-metric row per requested ESP. */
  perEsp: z.array(
    z.object({
      esp: espValueSchema,
      sentCount: z.number().nullable(),
      replyCount: z.number().nullable(),
      inboxCount: z.number().nullable(),
      otherCount: z.number().nullable(),
      spamInboxCount: z.number().nullable(),
      undeliveredCount: z.number().nullable(),
      deliverabilityRate: z.number().nullable(),
    }),
  ),
});

export const espAnalyticsSchema = z.object({
  /** See `espWarmupStatsSchema.locked`. The trend panel deliberately does NOT
   * blur on it — a locked chart renders its real series as normal. */
  locked: z.boolean(),
  granularity: z.enum(ESP_GRANULARITIES),
  /** The zone the buckets AND the window bounds were both computed in. */
  timeZone: z.string(),
  /**
   * Zero-filled — every bucket in the window is present, gaps included. Real
   * figures whether or not `locked`.
   */
  timeSeries: z.array(
    z.object({
      /** Bucket start as `YYYY-MM-DD`, already in the requested time zone. */
      date: z.string(),
      sentCount: z.number(),
      replyCount: z.number(),
      inboxCount: z.number(),
      otherCount: z.number(),
      spamCount: z.number(),
      undeliveredCount: z.number(),
      deliverabilityRate: z.number(),
    }),
  ),
});

export type EspWarmupStats = z.infer<typeof espWarmupStatsSchema>;
export type EspDeliverabilityScore = z.infer<typeof espDeliverabilityScoreSchema>;
export type EspDeliverabilityItem = EspDeliverabilityScore['perEsp'][number];
export type EspBreakdown = z.infer<typeof espBreakdownSchema>;
export type EspBreakdownItem = EspBreakdown['perEsp'][number];
export type EspAnalytics = z.infer<typeof espAnalyticsSchema>;
export type EspAnalyticsPoint = EspAnalytics['timeSeries'][number];

/**
 * Query parameters shared by all four routes.
 *
 * `to` is the INCLUSIVE end date — the server advances one local day past it to
 * build its exclusive upper bound (`ReportsService.resolveEspWindow`). Sending
 * tomorrow's date for "through today" would add an empty trailing bucket to
 * every chart.
 *
 * `timeZone` is REQUIRED here even though the API defaults it to `'UTC'`: day
 * boundaries are local and computed server-side from this value, so omitting it
 * silently buckets the whole page in UTC.
 */
export type EspAnalyticsParams = {
  /** Inclusive start, `YYYY-MM-DD`. */
  from: string;
  /** Inclusive end, `YYYY-MM-DD`. */
  to: string;
  /** Buckets to include. Omit for all four (the server's own default). */
  esps?: readonly EspValue[];
  /** IANA zone — `resolvedTimeZone()` from `lib/date`. */
  timeZone: string;
};

/**
 * Serialise the shared params. `esps` goes out as one repeated key per value
 * (what the DTO's `@Transform` normalises), pre-sorted so two selections that
 * differ only in click order produce one request and one cache entry — the same
 * normalisation `ReportsService.espCacheKey` does server-side.
 */
function espSearchParams(params: EspAnalyticsParams): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('from', params.from);
  sp.set('to', params.to);
  sp.set('timeZone', params.timeZone);
  for (const esp of [...(params.esps ?? [])].sort()) sp.append('esps', esp);
  return sp;
}

export async function getEspWarmupStats(
  accountId: number,
  params: EspAnalyticsParams,
): Promise<EspWarmupStats> {
  const raw = await apiFetch<unknown>(
    `/reports/warmup/esp/stats/${accountId}?${espSearchParams(params)}`,
  );
  return espWarmupStatsSchema.parse(raw);
}

export async function getEspDeliverabilityScore(
  accountId: number,
  params: EspAnalyticsParams,
): Promise<EspDeliverabilityScore> {
  const raw = await apiFetch<unknown>(
    `/reports/warmup/esp/deliverability-score/${accountId}?${espSearchParams(params)}`,
  );
  return espDeliverabilityScoreSchema.parse(raw);
}

export async function getEspBreakdown(
  accountId: number,
  params: EspAnalyticsParams,
): Promise<EspBreakdown> {
  const raw = await apiFetch<unknown>(
    `/reports/warmup/esp/breakdown/${accountId}?${espSearchParams(params)}`,
  );
  return espBreakdownSchema.parse(raw);
}

export async function getEspAnalytics(
  accountId: number,
  params: EspAnalyticsParams,
  granularity: EspGranularity,
): Promise<EspAnalytics> {
  const sp = espSearchParams(params);
  sp.set('granularity', granularity);
  const raw = await apiFetch<unknown>(`/reports/warmup/esp/analytics/${accountId}?${sp}`);
  return espAnalyticsSchema.parse(raw);
}

/**
 * Fires the backend's synchronous export pipeline (ClickHouse query → CSV
 * build → SMTP send) for the same window/esps/timeZone the four read panels
 * are currently showing. Nothing is downloaded here — the response is just an
 * ack; the CSV itself is emailed server-side. Same guard/entitlement stack as
 * the four `get*` functions above (`analyticsFull`).
 */
export async function postEspExportReport(
  accountId: number,
  params: EspAnalyticsParams,
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(
    `/reports/warmup/esp/export/${accountId}?${espSearchParams(params)}`,
    { method: 'POST' },
  );
}

/**
 * The `/email-accounts` listing page's "Export report" button — same
 * fire-a-synchronous-pipeline-that-just-emails-a-CSV contract as
 * {@link postEspExportReport} above, widened from one account to the
 * listing's current selection/filter scope. `selection` is the exact
 * `BulkSelection` envelope the bulk-action bar already sends (reused, not
 * re-declared); `esps` omitted means all three, matching the ESP family's
 * own default.
 */
export async function postAllAccountsWarmupExport(body: {
  selection: BulkSelection;
  days?: WarmupExportDays;
  from?: string;
  to?: string;
  esps?: readonly EspValue[];
}): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/reports/warmup-export', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
